import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { fromState } from "@/lib/nav";
import { useScrollMemory } from "@/hooks/useScrollMemory";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PhotoUploader } from "@/components/PhotoUploader";
import { PlaceInSectorDialog, undoPlacementToast } from "@/components/PlaceInSectorDialog";
import { MainPhotoCell } from "@/components/MainPhotoCell";
import { Plus, Truck, ScanLine, Loader2, Archive, Save, QrCode, AlertTriangle, MapPin, Search, Download, ArrowUpDown } from "lucide-react";
import { useDonorStatuses, useDonorStageLabel } from "@/lib/vehicleLists";
import { DonorTagButton } from "@/components/DonorTagButton";
import { DonorBatchTagDialog } from "@/components/DonorBatchTagDialog";
import { DonorDuplicatesDialog } from "@/components/DonorDuplicatesDialog";
import { TruckHealthBand } from "@/components/TruckHealthBand";
import { TruckFilterPanel } from "@/components/TruckFilterPanel";
import { MatchedLine } from "@/components/MatchBadge";
import { TruckRoleTag, TruckRoleFilter } from "@/components/TruckRoleTag";
import { useSearchEngine, RememberedSearchInput, SearchCameraButton, SearchGo } from "@/components/search/engine";
import { RecentSearches, SearchTabs, useSessionTab, useOwnTagOpen, useRecentSettle } from "@/components/search/SearchBox";
import { PartResults, DidYouMean, useTabCount } from "@/components/search/TabResults";
import { useRecentSearches } from "@/lib/recentSearches";
import { CaptureGapNote } from "@/components/search/OnDonorBand";
import { SyntheticDataBanner } from "@/components/SyntheticDataBanner";
import { TruckSpecFilters } from "@/components/search/TruckFilterRail";
import { ScanTextInput } from "@/components/ScanTextInput";

const EMPTY = { vin: "", vin_unknown: false, asset_class: "vehicle", needs_info: false, needs_info_note: "", status: "", truck_type: "", year: "", make: "", model: "", engine_model: "", mileage: "", hours: "", purchase_price: "", landed_cost: "", notes: "" };

// ── AI-capture chip: ONE glanceable indicator combining build-sheet + photos scrub.
// Worst state wins the color; a fully-done OR fully-empty row stays QUIET (recedes), so the
// eye is only pulled by rows that still need work. The two dimensions stay independently
// filterable (see the Sheet + Photos dropdowns) — the chip is for glancing, the filters for working.
const SHEET_SEG = {
  none:     { icon: "○", word: "Sheet",            tip: "No build sheet attached" },
  attached: { icon: "▣", word: "Sheet unread",     tip: "Build sheet attached — nobody has read it yet" },
  // READ EXACTLY (production, 29 Oct): 9 Freightliner sheets read cell by cell wore "Sheet unread"
  exact:    { icon: "✓", word: "Sheet",            tip: "Build sheet read exactly by its maker's reader — no AI" },
  scrubbed: { icon: "✓", word: "Sheet",            tip: "Build sheet read by AI" },
  // READ, AND IT SAID ALMOST NOTHING. His MUST CLOSE, 2026-10-13: a thin sheet used to wear the
  // same ✓ as a clean ten-page read, so the only place that knew was the desk report.
  thin:     { icon: "⚠", word: "Sheet thin",       tip: "AI read the build sheet but got almost nothing off it — needs a human look" },
  // READ FINE, AND NOTHING LANDED. Measured in production before shipping the first fix: of 43 read
  // sheets NONE were thin on the page, but two trucks held ZERO specs off a full transcript — one
  // of them reading ~2,107 characters a page. They wore this row's green tick. The job on these is
  // Extract, not a re-read, so it says so in its own words.
  landed_nothing: { icon: "⚠", word: "Nothing landed", tip: "The AI read the sheet fine, but almost nothing off it is on this truck — press Extract on the Ask AI screen" },
  failed:   { icon: "⚠", word: "Sheet failed",     tip: "AI read the build sheet but got nothing back" },
};
const PHOTO_SEG = {
  none:       { icon: "○", word: "Photos",         tip: "AI has not read any of this truck's photos yet" },
  read:       { icon: "✓", word: "Photos",         tip: "Every photo on this truck has been read by the AI" },
  // PARTIAL MEANS PARTIAL (his ruling). The chip says how many are still waiting, because a
  // truck that says "read" while photos sit unread is the exact lie this release removed.
  incomplete: { icon: "◐", word: "Photos partly",  tip: "Some photos have been read, some are still waiting" },
  // READ, BUT BY AN OLDER SCAN. A photo read before the scan could capture item lists and tag
  // part numbers still has information owing, so it counts as work to do rather than done.
  stale:      { icon: "↻", word: "Photos re-read", tip: "Every photo has been read, but by an older scan — one more read picks up the information it could not capture" },
  no_photos:  { icon: "–", word: "No photos",      tip: "There are no photos on this truck to read" },
};

// governing tone: red = a problem to fix, amber = waiting on a human, green = done (quiet), grey = nothing (quiet)
function captureTone(sheet, photos) {
  if (sheet === "failed") return "red";
  if (sheet === "thin" || sheet === "landed_nothing" || sheet === "attached" || photos === "incomplete" || photos === "stale") return "amber";
  if (sheet === "scrubbed" || sheet === "exact" || photos === "read") return "green";
  return "grey";
}
const TONE_CLS = {
  red:   "border-red-300 bg-red-50 text-[#B01E28]",
  amber: "border-amber-300 bg-amber-50 text-amber-700",
  green: "border-slate-200 bg-white text-emerald-600",   // quiet — done rows recede
  grey:  "border-slate-200 bg-white text-slate-400",      // quiet — never-had-a-sheet rows recede
};
const AiCaptureChip = ({ sheet = "none", photos = "none", unread = 0 }) => {
  const tone = captureTone(sheet, photos);
  const s = SHEET_SEG[sheet] || SHEET_SEG.none;
  const p = PHOTO_SEG[photos] || PHOTO_SEG.none;
  const dim = (state) => (state === "none" ? "opacity-50" : "");
  const left = unread > 0 ? `${unread} left` : "";
  return (
    <span data-testid={`ai-capture-chip-${tone}`} title={`${s.tip} · ${p.tip}${left ? ` (${left})` : ""}`}
      className={`inline-flex items-center gap-1 rounded-lg border px-1.5 py-0.5 text-[9px] font-semibold ${TONE_CLS[tone]}`}>
      <span data-testid={`ai-capture-sheet-${sheet}`} className={`inline-flex items-center gap-0.5 ${dim(sheet)}`}>
        <span aria-hidden>{s.icon}</span>{s.word}
      </span>
      <span aria-hidden className="opacity-40">·</span>
      <span data-testid={`ai-capture-photos-${photos}`} className={`inline-flex items-center gap-0.5 ${dim(photos)}`}>
        <span aria-hidden>{p.icon}</span>{p.word}
        {left && <span data-testid="ai-capture-photos-left" className="font-mono opacity-80">{left}</span>}
      </span>
    </span>
  );
};



export default function DonorsPage() {
  const { has, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const openDonor = (id) => { engine.reportOpened({ id }); navigate(`/donors/${id}`, { state: fromState(location, "Vehicles & Equipment") }); };
  const [searchParams, setSearchParams] = useSearchParams();
  // URL-backed filters — resume identically to Find a Part / All Parts.
  //
  // "all" IS THE DROPDOWNS' WORD FOR "NO FILTER", AND FREE TEXT IS NOT A DROPDOWN. His bug,
  // 2026-10-13: *"when I get to 'all', the box WIPES CLEAN"* — `q=all` was being deleted from the
  // URL by the line below, so the box read back empty halfway through "allison". Text parameters
  // are written exactly as typed; only the filters get the sentinel.
  const TEXT_PARAMS = new Set(["q"]);
  const setParam = (patch) => setSearchParams((prev) => {
    const n = new URLSearchParams(prev);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null || v === "" || v === false || (v === "all" && !TEXT_PARAMS.has(k))) n.delete(k);
      else n.set(k, v === true ? "1" : String(v));
    });
    // any filter change (anything other than paging itself) returns to page 1
    if (!("page" in patch)) n.delete("page");
    return n;
  }, { replace: true });
  const searchQ = searchParams.get("q") || "";
  const statusFilter = searchParams.get("status") || "all";
  // his rule, 27 Oct: scrapped trucks are hidden until this is on (one tap), same as gone parts on Find a Part
  const showGone = searchParams.get("gone") === "1";
  const order = searchParams.get("order") || "";
  const sortKey = order ? order.replace(/_(asc|desc)$/, "") : null;
  const sortDir = order.endsWith("_desc") ? "desc" : "asc";
  const canManage = has("manage_donors");
  const canPrintTags = has("generate_print_labels");
  const showCost = user?.role === "Admin" || has("view_acquisition_cost");
  const PAGE_SIZE = 50;
  const engine = useSearchEngine({ scope: "trucks", urlBacked: true, pageSize: PAGE_SIZE, box: "vehicles" });
  // The owner's pipeline, from the owner's list. This page used to hardcode seven LEGACY stages,
  // so it offered three that no longer exist and hid five that do — including Checked In.
  const stages = useDonorStatuses();
  const stageLabel = useDonorStageLabel();
  const stageCodes = stages.map((s) => s.code);
  const donors = engine.res?.items || [];
  const total = engine.res?.total || 0;
  const loading = engine.loading;
  // ready only once THIS search's rows are in — the engine is not "loading" for a beat before it starts
  useScrollMemory(!loading && engine.res != null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [decoding, setDecoding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState({});  // {donorId: true} for bulk stage change / batch print
  const [bulkStage, setBulkStage] = useState("");
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [batchTagOpen, setBatchTagOpen] = useState(false);
  const [notagBatchOpen, setNotagBatchOpen] = useState(false);
  const [vinWarn, setVinWarn] = useState(null);   // live check-vin preflight result
  const [dupCount, setDupCount] = useState(0);     // # of duplicate-VIN groups
  const [serialDups, setSerialDups] = useState({ groups: [] });  // the ROWS behind the duplicate-serial count
  const [dupItems, setDupItems] = useState([]);    // duplicate-VIN groups for the attention drawer
  const [noTagItems, setNoTagItems] = useState([]); // unprinted-tag units for batch-print preview
  const [dupOpen, setDupOpen] = useState(false);
  const [vinAudit, setVinAudit] = useState({ count: 0, items: [] });  // vehicles with a non-17-char / I·O·Q VIN
  const [nearVin, setNearVin] = useState([]);      // VIN pairs one character apart — merge_trucks only
  const [nearVinTotal, setNearVinTotal] = useState(0);
  const [asks, setAsks] = useState({ open: 0, items: [] });  // components somebody is asking about
  const [kpis, setKpis] = useState(null);          // command-center KPIs (/truck-kpis)
  const [origVin, setOrigVin] = useState(null);    // VIN when an edit opened → grandfather unchanged VINs
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleOne = (did) => setSelected((s) => ({ ...s, [did]: !s[did] }));
  const canSelect = canManage || canPrintTags || has("putaway");
  const applyBulkStage = async () => {
    setBulkSaving(true);
    try {
      const { data } = await api.post("/donors/bulk-status", { donor_ids: selectedIds, status: bulkStage });
      toast.success(`${data.updated} truck${data.updated === 1 ? "" : "s"} set to ${stageLabel(bulkStage)}`);
      setBulkConfirm(false); setSelected({}); engine.run();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkSaving(false); }
  };

  const pageNum = Math.max(1, parseInt(searchParams.get("page") || "1", 10));

  // data now comes from the shared useSearchEngine({scope:"trucks"}) instance (engine.res) —
  // one engine, no parallel /truck-search implementation on this page.
  const loadDupCount = async () => {
    try { const { data } = await api.get("/donors/duplicates"); setDupCount(data.total_groups || 0); setDupItems(data.groups || data.items || []); }
    catch { /* ignore */ }
  };
  const loadNoTag = async () => {
    if (!canPrintTags) return;
    try { const { data } = await api.get("/truck-search", { params: { tag_print: "unprinted", page_size: 100, facets: 0 } }); setNoTagItems(data.items || []); }
    catch { /* ignore */ }
  };
  const loadVinAudit = async () => {
    try { const { data } = await api.get("/donors/vin-audit"); setVinAudit({ count: data.count || 0, items: data.items || [] }); }
    catch { /* ignore */ }
  };
  // THE ROWS BEHIND THE DUPLICATE-SERIAL COUNT. The band counted the groups and the drawer row
  // went nowhere — the dead-end pattern. Same call the count is len() of, so they cannot disagree.
  const loadSerialDups = async () => {
    try { const { data } = await api.get("/truck-serial-duplicates"); setSerialDups({ groups: data.groups || [], capped: !!data.capped }); }
    catch { /* ignore */ }
  };
  // LOOK-ALIKE VINs, one character apart. Asked for ONLY by an account holding `merge_trucks`
  // (Admin by default): "if someone doesn't have the merge permission, they must not know it
  // exists — no near-VIN flags. Absent, not blocked." (owner ruling 2026-09-28). It does NOT go
  // on the Home band — "the Home band is for daily work"; this lives here and in /health.
  const loadNearVin = async () => {
    try { const { data } = await api.get("/truck-merge/candidates"); setNearVin(data.near_vin || []); setNearVinTotal(data.near_vin_total ?? (data.near_vin || []).length); }
    catch { /* ignore */ }
  };
  // COMPONENTS SOMEBODY IS ASKING ABOUT — demand picking the assessment work. It lives HERE and on
  // the component itself, not on the Home band: "the Home band is for daily work" and this is a
  // vehicle-shaped flag (owner ruling 2026-06-01).
  const loadAsks = async () => {
    try {
      const { data } = await api.get("/requests/assess/queue", { params: { state: "open", limit: 50 } });
      setAsks({ open: data.total || 0, items: data.items || [] });
    } catch { /* ignore */ }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/donors/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `vehicles-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Vehicle list exported to CSV");
    } catch (e) { toast.error("Export failed — check your permission (export_data)."); }
  };

  const loadKpis = async () => {
    try { const { data } = await api.get("/truck-kpis"); setKpis(data); } catch { /* ignore */ }
  };
  useEffect(() => {
    loadKpis(); loadNoTag();
    // these three feed ONLY the attention drawer inside the health band — without the
    // capability there is nothing to feed, so a crew member's page stops asking for them
    if (has("view_inventory_health")) { loadDupCount(); loadVinAudit(); loadSerialDups(); }
    if (has("merge_trucks")) loadNearVin();
    loadAsks();
    /* eslint-disable-next-line */
  }, []);

  // managed truck-type list + client-side search/filter
  const canManageTypes = has("manage_taxonomy") || has("manage_lists");
  const [truckTypes, setTruckTypes] = useState([]);
  const loadTypes = async () => {
    try { const { data } = await api.get("/truck-types"); setTruckTypes(data); } catch { /* ignore */ }
  };
  useEffect(() => { loadTypes(); }, []);
  const addTruckType = async () => {
    const name = window.prompt("New truck/equipment type name:");
    if (!name || !name.trim()) return;
    try { await api.post("/truck-types", { name: name.trim() }); toast.success(`Added type "${name.trim()}"`); loadTypes(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleSort = (key) => {
    const nextDir = sortKey === key && sortDir === "asc" ? "desc" : "asc";
    setParam({ order: `${key}_${nextDir}` });
  };
  const sortArrow = (key) => (sortKey !== key ? "" : sortDir === "asc" ? " ▲" : " ▼");

  // Server already filtered + sorted + paginated via /truck-search — render the page as-is.
  const sortedDonors = donors;
  // When best-match-first ranking is WINDOWED (a generic word like "transmission" can match the
  // whole fleet), the pager stops at the window's edge rather than offering pages that would come
  // back empty — and the window itself is disclosed on the page (NG-2).
  const rw = engine.res?.relevance_window;
  const totalPages = Math.max(1, Math.ceil((rw?.truncated ? rw.ranked : total) / PAGE_SIZE));
  // Is the list NARROWED right now? Decides whether an empty list means "nothing matched"
  // or "there are no trucks yet" — a zero must never blame the wrong thing.
  const params = Object.fromEntries(searchParams);
  const FILTER_KEYS = ["status", "truck_type", "category", "placement", "sector", "tag_print",
                       "sheet", "photos", "capture", "condition", "presence", "vin_flag", "decode_flag", "dataplate",
                       "make", "model", "engine", "transmission", "arrived", "year_from", "year_to", "needs_info", "truck_filters", "role"];
  const activeFilterCount = FILTER_KEYS.filter((k) => params[k] && params[k] !== "all").length;
  const filtersActive = !!(searchQ || activeFilterCount);
  const clearFilters = () => setParam({ q: "", status: "all", truck_type: "all", tag_print: "all", placement: "all",
    sector: "", category: "all", sheet: "", photos: "", capture: "", condition: "", presence: "", vin_flag: "", decode_flag: "", dataplate: "",
    make: "", model: "", engine: "", transmission: "", arrived: "", year_from: "", year_to: "", needs_info: false,
    order: "", truck_filters: "", gone: "", role: "", page: 1 });
  // A WORKLIST JUMP SHOWS EXACTLY THE ROWS BEHIND THE NUMBER.
  //
  // The health tiles and the attention rows carry FLEET-WIDE counts. Patching one filter on top
  // of whatever was already narrowed makes the number disagree with the list: clicking "no tag
  // 106" and then "uncategorized 65" landed on 44 — the intersection — which is precisely the
  // disagreement this page exists to remove. So a jump CLEARS every narrowing control and applies
  // only its own; the chip row then shows the one filter that is on, so nothing is hidden.
  const jumpTo = (patch) => setParam({ q: "", status: "all", truck_type: "all", tag_print: "all",
    placement: "all", sector: "", category: "all", sheet: "", photos: "", capture: "", condition: "", dataplate: "",
    presence: "", vin_flag: "", decode_flag: "", make: "", model: "", engine: "", transmission: "", arrived: "", year_from: "",
    // the tiles count every truck that isn't archived, scrapped included — so the jump shows them too (number = rows)
    year_to: "", needs_info: false, truck_filters: "", gone: "1", role: "", page: 1, ...patch });
  // THE WORKLIST TILE IS THE PATH INTO THE JOB. Handing him a filtered list he then works through
  // truck by truck is the dead end he named, so this opens the review on the first truck in the
  // queue; the save hands him the next one. The filter still exists in the panel for browsing.
  const startPresenceReview = async () => {
    try {
      const { data } = await api.get("/truck-search", { params: { presence: "unchecked", page_size: 1, order: "stock_asc", facets: 0 } });
      const first = (data.items || [])[0];
      if (!first) { toast.success("Every truck that lists components has been reviewed."); return; }
      navigate(`/donors/${first.id}?review=presence`, { state: fromState(location, "Vehicles & Equipment") });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // THE LAST SECTOR STAYS LOADED, PER PERSON, AND IT SAYS SO ON THE SCREEN. His words: "that's
  // what turns one-at-a-time from a chore into one tap each" — plus the two traps he named himself:
  //   · THE TABLETS ARE SHARED. The key carries the account, so Dave picking the tablet up after
  //     him does NOT inherit his sector — nothing is loaded until Dave loads one.
  //   · A WEEK-OLD LOADED SECTOR MUST NOT CATCH HIM OUT. Past 8 hours the strip turns red, says
  //     how long ago it was loaded, and one-tap is OFF until he says it is still right.
  // And a code is not a place: "ROY-NWC" means nothing standing in the yard, so every sector on
  // this page reads NAME (CODE) — just the code only when the code IS the name.
  const canPlace = has("putaway");
  const sectorWords = (s) => (s?.name && s.name !== s.code ? `${s.name} (${s.code})` : (s?.code || ""));
  const agoWords = (h) => (h < 1 ? "less than an hour ago" : h < 2 ? "an hour ago"
    : h < 24 ? `${Math.round(h)} hours ago` : h < 48 ? "yesterday" : `${Math.round(h / 24)} days ago`);
  const stickyKey = `fas_place_sticky:${user?.email || "anon"}`;
  const [sticky, setSticky] = useState(null);
  const [staleOk, setStaleOk] = useState(false);
  useEffect(() => {
    let v = null;
    try { v = JSON.parse(localStorage.getItem(stickyKey) || "null"); } catch { v = null; }
    setSticky(v);
    setStaleOk(false);
  }, [stickyKey]);
  const loadSticky = (sec) => {
    const v = { code: sec.code, name: sec.name || sec.code, at: new Date().toISOString() };
    setSticky(v); setStaleOk(true);
    try { localStorage.setItem(stickyKey, JSON.stringify(v)); } catch { /* private mode */ }
  };
  const clearSticky = () => {
    setSticky(null);
    try { localStorage.removeItem(stickyKey); } catch { /* private mode */ }
  };
  const stickyHrs = sticky?.at ? (Date.now() - new Date(sticky.at).getTime()) / 3600000 : 999;
  const stickyStale = !!sticky?.code && stickyHrs > 8 && !staleOk;
  const oneTapOn = canPlace && !!sticky?.code && !stickyStale;
  const placeOneTap = async (d) => {
    if (!oneTapOn) return;
    try {
      const { data } = await api.post(`/donors/${d.id}/place`, { location_scan: sticky.code, confirm: true });
      if (data.ok) { undoPlacementToast(data.batch_id, 1, engine.run, data.message); engine.run(); }
      else toast.error(data.message);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // "IS IT SELECTING THE 241 OR JUST THE PAGE I CAN SEE?" — two different actions, so they are two
  // different buttons and each one says which it is. THE SERVER HANDS BACK 100 ROWS AT A TIME: the
  // first cut of this asked for 1,000 and trusted the reply, so "Select all 104" quietly selected
  // 100. The number on the button has to be the number that ends up selected, or the button lies.
  const [selectingAll, setSelectingAll] = useState(false);
  const selectAllFiltered = async () => {
    setSelectingAll(true);
    try {
      const base = {};
      if (searchQ) base.q = searchQ;
      ["category", "status", "truck_type", "placement", "sector", "needs_info", "tag_print",
       "sheet", "photos", "capture", "condition", "presence", "vin_flag", "decode_flag", "dataplate", "truck_filters"]
        .forEach((k) => { if (params[k] && params[k] !== "all") base[k === "truck_filters" ? "filters" : k] = params[k]; });
      const ids = [];
      let reported = 0;
      for (let pg = 1; pg <= 25; pg++) {
        const { data } = await api.get("/truck-search", { params: { ...base, page: pg, page_size: 100, facets: 0 } });
        reported = data.total ?? reported;
        const batch = (data.items || []).map((i) => i.id);
        ids.push(...batch);
        if (batch.length < 100 || ids.length >= reported) break;
      }
      setSelected(Object.fromEntries(ids.map((id) => [id, true])));
      if (ids.length < reported) {
        toast.warning(`Selected the first ${ids.length} of ${reported} — place these, then press it again for the rest.`);
      } else {
        toast.success(`Selected all ${ids.length} truck${ids.length === 1 ? "" : "s"} matching this filter`);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSelectingAll(false); }
  };

  // STALE GUARD: the rows on screen belong to whatever query last reached the server. If the
  // box now says something else and nothing is in flight (typically: the server is unreachable),
  // the count must NOT claim those rows are results for the new words. The 1.2s delay skips the
  // normal type-then-search debounce so this never flickers while someone is typing.
  const resQ = engine.res?.for_q;
  const syntheticInResults = engine.res?.synthetic?.in_results || 0;
  // while a seeded fleet exists every count on this page states its share, including a zero
  // one — a bare number is the one that gets taken as real by accident (owner, 2026-09-23)
  const seedUp = !!kpis?.synthetic?.active;
  // OWNER'S DASHBOARD, not a search aid: OFF for every role by default, granted per
  // person (owner ruling 2026-09-24). The server omits the health payload too.
  const canSeeHealth = has("view_inventory_health");
  const qMismatch = resQ !== undefined && (resQ || "").trim() !== searchQ.trim() && !loading && !engine.err;
  const [stale, setStale] = useState(false);
  useEffect(() => {
    if (!qMismatch) { setStale(false); return; }
    const t = setTimeout(() => setStale(true), 1200);
    return () => clearTimeout(t);
  }, [qMismatch]);
  // select-all operates on the CURRENT page view
  const visibleIds = sortedDonors.map((d) => d.id);
  const allChecked = visibleIds.length > 0 && visibleIds.every((id) => selected[id]);
  // SEARCH S3 — one box, Trucks | Parts tabs, your recent searches; Enter opens only our own tag (his rule, 26 Oct)
  const recent = useRecentSearches();
  const addRecent = recent.add;
  const [tab, setTab] = useSessionTab("vehicles", "trucks");
  const sq = searchQ.trim();
  const onParts = !!sq && tab === "parts";
  const partsCount = useTabCount("parts", searchQ, { status: "available" });
  const openTruckRow = useCallback((d) => { addRecent(searchQ); openDonor(d.id); }, [addRecent, searchQ]); // eslint-disable-line react-hooks/exhaustive-deps
  const openPartRow = useCallback((p) => { addRecent(searchQ); navigate(`/parts/${p.id}`, { state: fromState(location, "Vehicles & Equipment") }); }, [addRecent, searchQ, navigate, location]);
  const openOwnTag = useOwnTagOpen("Vehicles & Equipment", "donor-search");
  const onEnter = (v) => {
    const t = (v || "").trim();
    if (!t) return;
    addRecent(t);
    setParam({ q: t });
    openOwnTag(t);
  };
  // a recent search or a "did you mean": one tap RUNS it
  const runQ = (v) => { const t = (v || "").trim(); if (!t) return; addRecent(t); setParam({ q: t }); };
  useRecentSettle(recent, engine.res?.for_q);
  const toggleAll = () => {
    if (allChecked) setSelected((s) => { const n = { ...s }; visibleIds.forEach((id) => delete n[id]); return n; });
    else setSelected((s) => ({ ...s, ...Object.fromEntries(visibleIds.map((id) => [id, true])) }));
  };
  // THE TABLE ONLY APPEARS WHEN THERE IS ROOM FOR IT (>=1280). At 1024 — the tablet he stands in
// the yard with — six fixed columns plus a sidebar squeezed the vehicle cell to one word per
// line and CLIPPED the Actions column off the card entirely. Below 1280 each truck is a
// labelled stacked card instead, which is what a tablet wants anyway.
  // THE ROW READS LEFT TO RIGHT: photo, then who the truck is, then the facts, then the buttons.
  // The photo was UNDER the details, which made every row as tall as the photo PLUS the text with
  // a gap in the middle (his words, 2026-10-07: "the row doesn't flow"). Six columns, not seven:
  // the stock number sits with the vehicle it belongs to, because a 9-character code never needed
  // its own column and that width is what the VIN line and the flags actually want.
  const GRID = "xl:grid-cols-[140px_minmax(0,1fr)_120px_140px_70px_150px] 2xl:grid-cols-[150px_minmax(0,1fr)_140px_160px_80px_170px]";

  // deep-link edit (?edit=ID) from the donor detail page
  useEffect(() => {
    const eid = searchParams.get("edit");
    if (!eid) return;
    api.get(`/donors/${eid}`).then(({ data }) => openEdit(data)).catch(() => {});
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete("edit"); return n; }, { replace: true });
    // eslint-disable-next-line
  }, [searchParams]);

  const openNew = () => { setEditId(null); setForm(EMPTY); setVinWarn(null); setOrigVin(null); setOpen(true); };
  const openEdit = (d) => {
    setEditId(d.id); setVinWarn(null); setOrigVin(d.vin || "");
    setForm({ vin: d.vin || "", vin_unknown: !!d.vin_unknown, asset_class: d.asset_class || "vehicle", status: d.status, truck_type: d.truck_type || "", year: d.year || "", make: d.make || "", model: d.model || "",
      engine_model: d.engine_model || "", mileage: d.mileage || "", hours: d.hours || "", purchase_price: d.purchase_price ?? "", landed_cost: d.landed_cost ?? "", notes: d.notes || "",
      needs_info: !!d.needs_info, needs_info_note: d.needs_info_note || "" });
    setOpen(true);
  };

  const checkVin = async () => {
    if (form.vin_unknown || !form.vin || form.vin.length < 6) { setVinWarn(null); return; }
    try {
      const { data } = await api.get("/donors/check-vin", { params: { vin: form.vin, exclude_id: editId || undefined } });
      setVinWarn(data.active || data.archived ? data : null);
    } catch { setVinWarn(null); }
  };

  // DECODE NEVER OVERWRITES A PERSON. On an EXISTING truck this is a server action that fills only
  // what is empty or still the decode's own, and reports what it left alone and why; the old code
  // here took the decoded engine "or else whatever was in the box", so the decode always won and a
  // correction could be undone silently. On a NEW truck there is nothing to protect, so the boxes
  // are filled in the form — but what the decode SAID is carried into the save, so the record
  // knows from the first minute.
  const decode = async () => {
    if (form.vin_unknown) { toast.info("This truck is flagged VIN-unknown — enter details manually."); return; }
    if (!form.vin || form.vin.length < 11) { toast.error("Enter a full VIN (11+ chars) to decode."); return; }
    setDecoding(true);
    try {
      if (editId) {
        const { data } = await api.post(`/donors/${editId}/apply-decode`, {});
        const dn = data.donor || {};
        setForm((s) => ({ ...s, year: dn.year || "", make: dn.make || "", model: dn.model || "", engine_model: dn.engine_model || "" }));
        toast.success(`${data.applied.length} filled in, ${data.protected.length} left as entered.`,
          { duration: 9000, description: data.protected.map((p) => `${p.label}: kept "${p.kept}" — ${p.why} (decode said "${p.decode_said}")`).join(" · ") || undefined });
        engine.run();
      } else {
        const { data } = await api.post("/donors/decode-vin", { vin: form.vin });
        if (data.decoded) {
          const f = data.fields || {};
          // WHAT THE PERSON TYPED WINS. This was `f.year || s.year` — the decode's value beat
          // anything already in the box, so typing the right engine and then pressing Decode threw
          // it away. On a new truck the boxes start empty, so a decode still fills them all.
          setForm((s) => ({ ...s, year: s.year || f.year || "", make: s.make || f.make || "",
            model: s.model || f.model || "", engine_model: s.engine_model || f.engine_model || "",
            vin_decode: { vin: form.vin, fields: f },
            vin_extra: { ...s.vin_extra, ...Object.fromEntries(Object.entries(f).filter(([k]) => !["year", "make", "model", "engine_model"].includes(k))) } }));
          toast.success("VIN decoded from NHTSA — check it, then save");
        } else {
          toast.warning(data.error || "VIN not decoded — enter details manually");
        }
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDecoding(false); }
  };

  const save = async (allowArchivedVin = false) => {
    // VEHICLE VIN GATE (client layer): block save unless the VIN is exactly 17 chars (no I/O/Q).
    // Equipment is exempt. Existing records are GRANDFATHERED — the rule only fires on CREATE or
    // when the VIN is actually being CHANGED, so opening a legacy bad-VIN truck to edit other
    // fields never locks you out (it just surfaces in the VIN audit until you fix it).
    const vinChanged = !editId || (form.vin || "").trim().toUpperCase() !== (origVin || "").trim().toUpperCase();
    if (form.asset_class !== "equipment" && !form.vin_unknown && vinChanged) {
      const v = (form.vin || "").trim().toUpperCase();
      const issues = [];
      if (v.length !== 17) issues.push(`VIN is ${v.length} characters — a vehicle VIN must be exactly 17.`);
      const bad = ["I", "O", "Q"].filter((c) => v.includes(c));
      if (bad.length) issues.push(`VIN contains ${bad.join(", ")} — the letters I, O and Q are never used in a VIN.`);
      if (issues.length) { toast.error(issues.join(" ")); return; }
    }
    setSaving(true);
    const body = { ...form, vin_unknown: !!form.vin_unknown, allow_archived_vin: allowArchivedVin,
      truck_type: form.truck_type || null, purchase_price: form.purchase_price === "" ? null : Number(form.purchase_price),
      landed_cost: form.landed_cost === "" ? null : Number(form.landed_cost), mileage: form.mileage || null, hours: form.hours || null };
    try {
      if (editId) { await api.put(`/donors/${editId}`, body); toast.success("Truck updated"); }
      else { await api.post("/donors", body); toast.success("Truck created with stock number"); }
      setOpen(false); engine.run(); loadDupCount(); loadVinAudit(); loadKpis();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det && typeof det === "object" && det.kind === "active_vin") {
        toast.error(det.msg, { duration: 10000, action: det.existing_id ? { label: "Open truck", onClick: () => { setOpen(false); navigate(`/donors/${det.existing_id}`); } } : undefined });
      } else if (det && typeof det === "object" && det.kind === "archived_vin") {
        if (window.confirm(`${det.msg}\n\n(A scrapped/archived truck had this VIN. Click OK to add it as a NEW truck anyway.)`)) {
          setSaving(false); return save(true);
        }
      } else {
        toast.error(formatApiErrorDetail(det));
      }
    }
    finally { setSaving(false); }
  };

  const addDonorPhoto = () => { engine.run(); };

  const archive = async (d) => {
    if (!window.confirm(`Archive donor ${d.stock_number}? (soft delete)`)) return;
    try { await api.delete(`/donors/${d.id}`); toast.success("Truck archived"); engine.run(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="donors-page">
      <SyntheticDataBanner testid="donors-synthetic-banner" />
      <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold tracking-tight">Vehicles &amp; Equipment</h1>
          <p className="text-sm text-slate-500">Every vehicle and piece of equipment — autos, light/medium/heavy trucks, RVs, trailers, boats and gear. Add it here, decode the VIN, then upload photos to sort into parts.</p>
          {/* THE UNITS HEADLINE STAYS ON THE SURFACE; the ten stage pills that used to sit beside
              it moved into Filters, where every other way of narrowing already lives. */}
          {typeof kpis?.total === "number" && (
            <p data-testid="donor-fleet-total" className="mt-1 text-sm font-bold text-slate-800">
              {kpis.total} units
              {seedUp && <b data-testid="donor-fleet-total-synth" className="ml-1 font-mono text-[10px] text-[#B01E28]">({kpis.synthetic?.synthetic || 0} SYNTHETIC)</b>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {has("export_data") && (
            <Button data-testid="export-vehicles-csv" variant="outline" onClick={exportCsv}
              className="rounded-lg border-slate-300">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
          )}
          {canManage && (
            <Button data-testid="add-donor-button" onClick={openNew} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Add Truck
            </Button>
          )}
        </div>
      </div>


      {/* WHAT'S WRONG WITH THE FLEET — an OWNER'S DASHBOARD, permission-gated and OFF by default
          (owner ruling 2026-09-24). The crew comes here to find a truck; seven counts above the
          search box is a wall in front of that job. Off means ABSENT — no gap, no "you don't have
          permission" — so a crew member's page reads as a complete search page. The server omits
          the health payload entirely for them, so this isn't a hidden control that still answers. */}
      {canSeeHealth && (
        <TruckHealthBand
          kpis={kpis}
          params={params}
          dupItems={canManage ? dupItems : []}
          vinAudit={canManage ? vinAudit : { count: 0, items: [] }}
          nearVin={nearVin} nearVinTotal={nearVinTotal}
          asks={asks}
          serialDups={canManage ? serialDups : { groups: [] }}
          onMerged={() => {
            /* `load()` here was a stale name left behind when this page moved to the shared search
               engine — it threw a ReferenceError on the FIRST statement, so after a successful
               merge from this drawer the list, the KPIs and the duplicate counts all failed to
               refresh and the folded-away truck stayed on screen. The merge itself had already
               happened server-side; it just looked like it hadn't. */
            engine.run(); loadKpis(); loadDupCount(); loadNearVin(); loadSerialDups();
          }}
          canPrintTags={canPrintTags}
          onCategory={(v) => jumpTo({ category: v })}
          onJump={jumpTo}
          onReview={startPresenceReview}
        />
      )}
      {canSelect && selectedIds.length > 0 && (
        <div data-testid="bulk-stage-bar" className="flex flex-wrap items-center gap-3 mb-3 rounded-xl border border-[#E0781A]/40 bg-amber-50 px-4 py-2.5">
          <span className="text-sm font-semibold text-slate-700" data-testid="bulk-selected-count">
            {selectedIds.length} selected{allChecked && selectedIds.length <= visibleIds.length && total > visibleIds.length ? " — just this page" : ""}
          </span>
          {canPrintTags && (
            <Button data-testid="bulk-print-tags" size="sm" onClick={() => setBatchTagOpen(true)} className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white h-8">
              <QrCode className="w-3.5 h-3.5 mr-1.5" />Print Tags ({selectedIds.length})
            </Button>
          )}
          {canManage && (
            <>
              <span className="text-xs text-slate-500 border-l border-amber-300 pl-3">Set dismantle stage:</span>
              <Select value={bulkStage} onValueChange={setBulkStage}>
                <SelectTrigger data-testid="bulk-stage-select" className="h-8 w-48 rounded-lg border-slate-300 bg-white text-sm"><SelectValue placeholder="Choose a stage" /></SelectTrigger>
                <SelectContent className="rounded-lg">{stageCodes.map((s) => <SelectItem key={s} value={s} data-testid={`bulk-stage-opt-${s}`}>{stageLabel(s)}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid="bulk-stage-apply" size="sm" disabled={!bulkStage} onClick={() => setBulkConfirm(true)} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-8 disabled:opacity-50">Apply to {selectedIds.length}</Button>
            </>
          )}
          {/* PLACING RIDES ON `putaway`, NOT ON "can edit trucks". It sat inside the manage-trucks
              block, so somebody who can move stock but not edit a truck record got the selection
              bar with no Place button in it — the same built-but-unreachable shape as the on-donor
              band and the review block. The server gate is `putaway`; so is this. */}
          {canPlace && (
            <PlaceInSectorDialog donorIds={selectedIds} onPlaced={loadSticky}
              onDone={() => { setSelected({}); engine.run(); }}
              trigger={<Button data-testid="bulk-place-sector" size="sm" variant="outline" className="rounded-lg border-slate-300 bg-white h-8"><MapPin className="w-3.5 h-3.5 mr-1.5" />Place in sector ({selectedIds.length})</Button>} />
          )}
          {total > selectedIds.length && (
            <Button data-testid="bulk-select-all-filtered" size="sm" variant="ghost" disabled={selectingAll}
              onClick={selectAllFiltered}
              className="rounded-lg h-8 text-xs text-[#B01E28] hover:bg-red-50">
              {selectingAll ? "Selecting…" : `Select all ${total} matching this filter`}
            </Button>
          )}
          <button data-testid="bulk-stage-clear" onClick={() => setSelected({})} className="text-xs text-slate-500 hover:text-slate-800 underline ml-auto">Clear</button>
        </div>
      )}

      {/* TWO STANDING CONTROLS. This row held ten: a search box, seven dropdowns, a toggle and a
          clear link — ten decisions before you reached a truck. Every option survives, inside one
          panel; what changed is that the COUNTS moved out to the health tiles (a number belongs in
          exactly one place, and a tile you can click beats an option you have to read), and the
          spec filters no longer hide behind having picked a category first. */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-[16rem] sm:max-w-xl">
          <form className="relative flex-1"
            onSubmit={(e) => { e.preventDefault(); onEnter(e.currentTarget.querySelector("input")?.value || ""); }}>
            <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <RememberedSearchInput testid="donor-search" value={searchQ} onChange={(v) => setParam({ q: v })}
              enterKeyHint="search" autoComplete="off"
              placeholder="Stock #, VIN, make, model, spec value, serial…" className="rounded-xl border-slate-300 bg-white h-12 pl-11 text-base shadow-sm" />
          </form>
          {/* S5: Enter or this button runs the search — never while typing */}
          <SearchGo testid="donor-search" onGo={(v) => { setParam({ q: v.trim() }); onEnter(v); }} size="h-12" />
          {/* beside the form, not in it: the camera panel has its own form */}
          <SearchCameraButton testid="donor-search" onScan={(v) => onEnter(v)} size="h-12" />
        </div>
        <TruckFilterPanel
          params={params}
          total={total}
          stageCodes={stageCodes}
          stageLabel={stageLabel}
          stages={kpis?.pipeline || []}
          fleetTotal={kpis?.total}
          facets={engine.res?.stage_facets}
          vinFacets={engine.res?.vin_facets}
          decodeFacets={engine.res?.decode_facets}
          dataplateFacets={engine.res?.dataplate_facets}
          vehicleFacets={engine.res?.vehicle_facets}
          tagFacets={kpis?.capture?.tag_print}
          truckTypes={truckTypes}
          categories={kpis?.categories || []}
          offPipeline={kpis?.off_pipeline}
          canPrintTags={canPrintTags}
          activeCount={activeFilterCount}
          seedUp={!!kpis?.synthetic?.active}
          onParam={setParam}
          onClear={clearFilters}
        >
          {(eng) => <TruckSpecFilters engine={eng} />}
        </TruckFilterPanel>
        {/* SORTING THAT WORKS ON A TABLET (his ask, 2026-10-13). Sorting lived ONLY in the table
            column headers, and the table header does not exist below a desk-width screen — so on
            the tablet he carries round the yard the list could not be sorted at all. Same `order`
            the headers write, so a sort survives being picked on either screen, and the headers
            still show which way it went. */}
        <div className="xl:hidden flex items-center gap-1.5">
          <ArrowUpDown className="w-4 h-4 text-slate-400 shrink-0" />
          <Select value={order || (searchQ.trim() ? "relevance" : "newest")}
            onValueChange={(x) => setParam({ order: x === "relevance" ? "" : x, page: 1 })}>
            <SelectTrigger data-testid="donor-sort-select" className="h-10 w-[12.5rem] rounded-lg border-slate-300 bg-white text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-lg">
              {searchQ.trim() && <SelectItem value="relevance" data-testid="donor-sort-opt-relevance">Best matches first</SelectItem>}
              <SelectItem value="newest" data-testid="donor-sort-opt-newest">Changed most recently</SelectItem>
              <SelectItem value="oldest" data-testid="donor-sort-opt-oldest">Untouched longest</SelectItem>
              <SelectItem value="vehicle_desc" data-testid="donor-sort-opt-vehicle_desc">Year — newest truck first</SelectItem>
              <SelectItem value="vehicle_asc" data-testid="donor-sort-opt-vehicle_asc">Year — oldest truck first</SelectItem>
              <SelectItem value="stock_asc" data-testid="donor-sort-opt-stock_asc">Stock # — A to Z</SelectItem>
              <SelectItem value="stock_desc" data-testid="donor-sort-opt-stock_desc">Stock # — Z to A</SelectItem>
              <SelectItem value="status_asc" data-testid="donor-sort-opt-status_asc">Dismantle stage</SelectItem>
              {showCost && <SelectItem value="cost_desc" data-testid="donor-sort-opt-cost_desc">Cost — highest first</SelectItem>}
              {showCost && <SelectItem value="cost_asc" data-testid="donor-sort-opt-cost_asc">Cost — lowest first</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        <span data-testid="donor-result-count" className="text-xs font-mono self-center sm:ml-auto">
          {stale
            ? <span data-testid="donor-result-stale" className="text-[#C76714]">
                Not searched yet — the <b>{total}</b> row{total === 1 ? " below is the last result" : "s below are the last results"}
                {resQ ? <> for “{resQ}”</> : " for all trucks"}
              </span>
            : searchQ.trim()
              ? <span className="text-slate-600">Showing <b className="text-slate-900">{total}</b> result{total === 1 ? "" : "s"} for <span className="text-[#B01E28] font-semibold">“{searchQ.trim()}”</span>{seedUp ? <b data-testid="donor-synthetic-in-results" className={`ml-1.5 ${syntheticInResults ? "text-[#B01E28]" : "text-slate-400"}`}>({syntheticInResults} SYNTHETIC)</b> : null}{!sortKey && <span className="ml-1.5 text-slate-400">· best matches first</span>}</span>
              : <span className="text-slate-400">{total}{typeof kpis?.total === "number" && total !== kpis.total ? ` of ${kpis.total}` : ""} match{total === 1 ? "" : "es"}{seedUp ? <b data-testid="donor-synthetic-in-results" className={`ml-1 ${syntheticInResults ? "text-[#B01E28]" : "text-slate-400"}`}>({syntheticInResults} SYNTHETIC)</b> : null}</span>}
        </span>
        {/* BUILDERS · PARTS · ALL (his ask, Batch 4) — the truck's existing role, tap only, with counts */}
        <TruckRoleFilter value={params.role} counts={engine.res?.role_facets} testid="donor-role-filter"
          onPick={(k) => setParam({ role: k, page: 1 })} />
        {statusFilter === "all" && (showGone || engine.res?.gone_hidden?.count > 0) && (
          <button type="button" data-testid="donor-gone-toggle" onClick={() => setParam({ gone: showGone ? "" : "1" })}
            title="Scrapped trucks are hidden by default — tap to include them, tap again to hide them"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 h-9 text-xs font-semibold self-center transition-colors ${showGone ? "border-slate-700 bg-slate-800 text-white" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>
            <Archive className="w-3.5 h-3.5" />
            {showGone ? "Showing scrapped — hide them"
              : `Show ${(engine.res.gone_hidden.stages || ["scrapped"]).join(" & ").toLowerCase()} (${engine.res.gone_hidden.count})`}
          </button>
        )}
        {/* THE PAGE'S TICK-BOX LIVES IN THE TABLE HEADER, AND THERE IS NO TABLE HEADER ON A
            TABLET. Without this, bulk placing was a desk-only job on the exact screen he is
            standing in the yard with. Says which trucks it means, out loud. */}
        {canSelect && visibleIds.length > 0 && (
          <button type="button" data-testid="select-this-page" onClick={toggleAll}
            className="xl:hidden text-xs font-semibold text-[#B01E28] underline self-center">
            {allChecked ? "Unselect these" : `Select these ${visibleIds.length}`} on this page
          </button>
        )}

      </div>
      <RecentSearches recent={recent} onPick={runQ} testid="donor-search-recent" />
      {sq && <div className="mt-2"><SearchTabs tab={tab} onTab={setTab} order={["trucks", "parts"]}
        counts={{ trucks: resQ === searchQ && !loading ? { count: total } : null, parts: partsCount }} /></div>}

      {onParts ? <PartResults q={sq} onOpen={openPartRow} onPick={runQ} /> : (<>
      {engine.err && (
        <div data-testid="donor-search-refused" className="mb-3 rounded-xl border border-[#B01E28]/40 bg-red-50 p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-[#B01E28] mt-0.5 shrink-0" />
          <div className="text-xs text-[#B01E28] leading-relaxed">
            <b className="block text-[11px] font-mono uppercase tracking-wider mb-0.5">Search refused — nothing was filtered</b>
            {engine.err}
          </div>
        </div>
      )}

      {rw?.truncated && (
        <div data-testid="donor-relevance-window" className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
          Best-match-first ordering covers the first <b className="text-slate-800">{rw.ranked.toLocaleString()}</b> of{" "}
          <b className="text-slate-800">{rw.total.toLocaleString()}</b> matches — so paging stops there. Add a word, use a
          spec filter, or sort a column to reach the rest.
        </div>
      )}

      {/* How much of the fleet could NOT have answered this search. Shown on thin results as
          well as empty ones — two hits while 52 trucks have no engine recorded looks like an
          answer and isn't. Same component, same words, on every surface that searches trucks. */}
      {!stale && engine.res?.capture_gap && (
        <div className="mb-3"><CaptureGapNote gap={engine.res.capture_gap} testid="donor-capture-gap" /></div>
      )}

      {canPlace && sticky?.code && (
        <div data-testid="place-sticky-bar"
          className={`mb-3 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 ${stickyStale ? "border-[#B01E28] bg-red-50" : "border-[#E0781A]/40 bg-orange-50"}`}>
          <MapPin className={`w-3.5 h-3.5 ${stickyStale ? "text-[#B01E28]" : "text-[#C76714]"}`} />
          {stickyStale ? (
            <>
              <span data-testid="place-sticky-stale" className="text-xs text-[#B01E28]">
                You loaded <b>{sectorWords(sticky)}</b> <b>{agoWords(stickyHrs)}</b> — still placing into that
                one? One-tap is off until you say so.
              </span>
              <Button data-testid="place-sticky-reconfirm" size="sm" onClick={() => setStaleOk(true)}
                className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white h-7 text-xs">
                Yes — still {sticky.code}
              </Button>
            </>
          ) : (
            <span data-testid="place-sticky-target" className="text-xs text-slate-700">
              <b className="uppercase tracking-wide text-[#C76714]">Placing into:</b> <b>{sectorWords(sticky)}</b>.
              One tap on a row's <b className="font-mono">{sticky.code}</b> button puts that truck there.
            </span>
          )}
          <button data-testid="place-sticky-clear" onClick={clearSticky}
            className="ml-auto text-[11px] underline text-slate-500 hover:text-slate-800">clear it</button>
        </div>
      )}
      <div className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
        <div className={`hidden xl:grid ${GRID} gap-2 bg-slate-50 px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100`}>
          <span className="truncate">Photo</span>
          <span className="flex items-center gap-2 min-w-0">
            {canSelect && <input type="checkbox" data-testid="bulk-select-all" checked={allChecked} onChange={toggleAll}
              title={`Select these ${visibleIds.length} — the trucks on this page`}
              aria-label={`Select these ${visibleIds.length} on this page`}
              className="w-3.5 h-3.5 accent-[#E0781A] cursor-pointer shrink-0" />}
            <button data-testid="sort-stock" onClick={() => toggleSort("stock")} className="uppercase truncate hover:text-slate-800 transition-colors">Stock{sortArrow("stock")}</button>
            <span className="text-slate-300">·</span>
            <button data-testid="sort-vehicle" onClick={() => toggleSort("vehicle")} className="text-left uppercase truncate min-w-0 hover:text-slate-800 transition-colors">Vehicle / VIN{sortArrow("vehicle")}</button>
          </span>
          <span className="truncate">Type</span>
          <button data-testid="sort-status" onClick={() => toggleSort("status")} className="text-left uppercase truncate min-w-0 hover:text-slate-800 transition-colors">Status{sortArrow("status")}</button>
          {showCost ? <button data-testid="sort-cost" onClick={() => toggleSort("cost")} className="text-left uppercase hover:text-slate-800 transition-colors">Cost{sortArrow("cost")}</button> : <span />}
          <span className="text-right">Actions</span>
        </div>
        {loading ? <div className="p-8 text-center font-mono text-xs text-slate-400">LOADING…</div>
          : engine.err ? <div className="p-8 text-center font-mono text-xs text-[#B01E28]">SEARCH REFUSED — see the message above</div>
          : donors.length === 0 && filtersActive ? (
            <div data-testid="donor-no-match" className="p-8 text-center">
              <div className="font-mono text-xs uppercase tracking-wider text-slate-500">No trucks match</div>
              {searchQ.trim()
                ? <div className="mt-2 text-xs text-slate-500 max-w-xl mx-auto">
                    {engine.res?.did_you_mean?.length > 0 && (
                      <div className="mb-4"><DidYouMean q={searchQ.trim()} options={engine.res.did_you_mean} onPick={runQ} testid="donor-dym" /></div>
                    )}
                    Nothing matched <b className="text-slate-800">“{searchQ.trim()}”</b> in stock #, VIN, vehicle name,
                    spec values, spec names, component numbers or factory options.
                    <span className="block mt-1 text-[11px] text-slate-400">
                      Build-sheet text is not searchable yet. Try one word instead of several, or drop the keyword and
                      use a spec filter.
                    </span>
                  </div>
                : <p className="mt-2 text-xs text-slate-500">Your filters ruled every truck out — loosen one to see results.</p>}
              <button type="button" data-testid="donor-no-match-clear" onClick={clearFilters}
                className="mt-4 min-h-12 px-4 text-sm font-semibold text-[#B01E28] hover:underline">Clear search &amp; filters</button>
            </div>
          )
          : donors.length === 0 ? <div className="p-8 text-center font-mono text-xs text-slate-400">NO TRUCKS — add one or load sample data</div>
          : sortedDonors.map((d) => (
            <div key={d.id} data-testid={`donor-row-${d.stock_number}`} data-back-row className={`flex items-start gap-3 xl:grid ${GRID} xl:gap-2 xl:items-center px-4 py-3.5 border-t border-slate-100 hover:bg-slate-50/70 transition-colors`}>
              {/* ONE PHOTO PER TRUCK, ON THE LEFT — not a 12-thumb strip, and not under the text.
                  50 rows x 12 thumbs was up to 600 thumbnail requests for one page and a working
                  set bigger than the per-pod cache. SIZE: 64px was too small to recognise a truck
                  by (his words, 2026-10-06), so the cell is 112px on a phone and 128px from tablet
                  up — 4x the area. It stays on the SMALL `/thumb` variant (400px max edge, quality
                  72), never the full original: a 128px box needs 384px even on a 3x-density tablet
                  screen, so nothing is being stretched. The photo IS the control — the count sits
                  on it, so there is one thing to tap instead of a photo and a chip that did the
                  same job. `xl:contents` dissolves these two wrappers on a desktop grid so the
                  cells below are grid children, while a tablet gets photo-left / details-right. */}
              <div className="shrink-0 xl:contents">
                <MainPhotoCell recordId={d.id} stockNumber={d.stock_number} main={d.main_photo}
                  photoCount={d.photo_count || 0} onChanged={() => engine.run()}
                  size="w-28 h-28 sm:w-32 sm:h-32" countOverlay
                  testidPrefix={`donor-main-photo-${d.stock_number}`} />
              </div>
              {/* TABLET: photo on the left, then the truck's identity, a BAND of facts that wraps
                  across the width instead of stacking one per line, and the buttons. Every
                  wrapper here is `xl:contents`, so on the desktop grid they all dissolve and the
                  six cells line up under their own headers. */}
              <div className="min-w-0 flex-1 flex flex-col gap-2 lg:flex-row lg:items-start lg:gap-4 xl:contents">
              {/* WHO THE TRUCK IS, in one cell: the stock number and the vehicle it belongs to. */}
              <div className="min-w-0 lg:flex-1">
              <span className="flex items-center gap-2">
                {canSelect && <input type="checkbox" data-testid={`donor-check-${d.stock_number}`} checked={!!selected[d.id]} onChange={() => toggleOne(d.id)} onClick={(e) => e.stopPropagation()} className="w-3.5 h-3.5 accent-[#E0781A] cursor-pointer shrink-0" />}
                <span data-testid={`donor-open-${d.stock_number}`} onClick={() => openDonor(d.id)} className="font-mono text-xs font-bold flex items-center gap-1 cursor-pointer hover:text-[#B01E28] transition-colors truncate"><Truck className="w-3.5 h-3.5 text-[#B01E28] shrink-0" />{d.stock_number}</span>
              </span>
              <span className="text-sm cursor-pointer min-w-0 block" onClick={() => openDonor(d.id)}>
                <span className="font-medium hover:underline">{[d.year, d.make, d.model].filter(Boolean).join(" ") || "—"}</span>
                <span className="block font-mono text-[11px] truncate">{d.vin ? <span className="text-slate-700 font-semibold">VIN {d.vin}</span> : <span className="text-slate-300">No VIN</span>}{d.engine_model ? <span className="text-slate-400"> · {d.engine_model}</span> : ""}</span>
                {/* THE VIN FLAG, ON THE ROW ITSELF, so a filtered worklist can be read without
                    opening fourteen trucks. SHORT on the row and the full sentence on hover.
                    Only ONE of the three short-VIN cases is work, so only that one is amber: a
                    pre-1981 VIN and a plate already recorded as unreadable are grey facts.
                    Nothing here changes a VIN. */}
                {d.vin_check && !d.vin_check.ok && (
                  <span data-testid={`donor-row-vin-flag-${d.stock_number}`} title={d.vin_check.note}
                    className={`block text-[11px] leading-snug ${["pre_1981", "vin_unknown"].includes(d.vin_check.kind) ? "text-slate-400" : "text-[#B01E28]"}`}>
                    {d.vin_check.reason === "check_digit"
                      ? <>⚠ VIN check digit doesn't match — expected <b>{d.vin_check.expected}</b> at position 9, not <b>{d.vin_check.found}</b></>
                      : d.vin_check.reason === "forbidden_chars" ? <>⚠ VIN contains I, O or Q — never in a VIN</>
                      : d.vin_check.kind === "pre_1981" ? <>Short VIN ({d.vin_check.length}) — normal before 1981</>
                      : d.vin_check.kind === "vin_unknown" ? <>Partial VIN ({d.vin_check.length} of 17) — recorded as VIN-unknown, plate unreadable</>
                      : <>⚠ Partial VIN — {d.vin_check.length} of 17 characters</>}
                  </span>
                )}
                {/* HIS ADDITION: the sticker on that truck is now wrong, so it says so wherever
                    the truck is listed — not only when the reprint filter is on. */}
                {d.tag_stale_at && (
                  <span data-testid={`donor-row-tag-stale-${d.stock_number}`}
                    className="block text-[11px] leading-snug font-semibold text-amber-700">
                    ⚠ Tag out of date — reprint
                    {(d.tag_stale_fields || []).length
                      ? ` (${d.tag_stale_fields.join(", ")} corrected since)` : ""}
                  </span>
                )}
                {/* THE ENGINE, SPELLED OUT ON THE ROW, while the decode worklist is the filter
                    that is on — the engine is the thing being checked, and on this width it is
                    otherwise truncated into the VIN line. Same idea as the VIN flag above: a
                    filtered worklist has to be readable without opening thirty trucks. */}
                {params.decode_flag === "engine_unchecked" && !d.engine_model && (
                  <span data-testid={`donor-row-no-engine-${d.stock_number}`}
                    className="block text-[11px] leading-snug text-[#B01E28] font-semibold">
                    No engine entered — open the truck to add it
                  </span>
                )}
                {params.decode_flag && d.engine_model && (
                  <span data-testid={`donor-row-engine-${d.stock_number}`}
                    className="block text-[11px] leading-snug text-slate-600">
                    Engine <b className="font-mono text-slate-800">{d.engine_model}</b>
                    {params.decode_flag === "corrected"
                      ? " — corrected by a person"
                      : " — nobody has put their name to it"}
                  </span>
                )}
                {/* THE FRACTION, NOT A VERDICT. "0 of 53 specs for Heavy Truck" on a truck that
                    arrived this morning is a fact, not a failure — so it's stated in the same
                    grey as the VIN, with no warning colour and no apology. The denominator is
                    that truck's own vehicle type, which is the only reading that means anything. */}
                {d.capture && (
                  <span data-testid={`donor-capture-${d.stock_number}`} className="block text-[11px] text-slate-500">
                    {d.capture.defined > 0
                      ? <><b className="text-slate-700 tabular-nums">{d.capture.recorded}</b> of{" "}
                          <b className="text-slate-700 tabular-nums">{d.capture.defined}</b> specs for{" "}
                          {d.capture.category_label}</>
                      : <span className="text-slate-400">no spec fields defined for {d.capture.category_label} — nothing to measure against</span>}
                    {d.capture.off_set > 0 && (
                      <span data-testid={`donor-capture-offset-${d.stock_number}`} className="text-[#B01E28]">
                        {" "}· {d.capture.off_set} value{d.capture.off_set === 1 ? "" : "s"} this type doesn't define
                      </span>
                    )}
                  </span>
                )}
                <MatchedLine match={d.match} kind="truck" testid={`donor-match-why-${d.stock_number}`} />
              </span>
              </div>
              {/* THE MIDDLE OF THE ROW WAS EMPTY while chips stacked four deep on the right (his
                  words: "use the empty space for the other items instead of leaving gaps"). The
                  AI/sheet state and the tag state are FACTS about the truck, so they live here
                  beside its type — the right-hand column is for things you press. */}
              {/* FROM lg UP (landscape tablet and desktop): identity on the left, the facts and
                  the buttons RIGHT-ALIGNED BESIDE IT rather than under it. Stacking them under
                  the identity put a near-empty button line at the bottom of every row and made a
                  landscape-tablet row 228px against a 128px photo — the wasted space he pointed
                  at. Side by side, a row is as tall as the TALLER column, not the sum of three.
                  BELOW lg it stays stacked on purpose: a portrait tablet has ~580px of content
                  width, and squeezing the identity into what is left after the buttons wrapped
                  the VIN onto four lines and made the row WORSE (measured: 597px). */}
              <div className="flex flex-col gap-2 lg:shrink-0 lg:items-end xl:contents">
              <div className="flex flex-wrap items-start gap-x-5 gap-y-1 lg:justify-end xl:contents">
              <div className="flex flex-col items-start gap-1 min-w-0">
                <TruckRoleTag role={d.role} testid={`donor-role-${d.stock_number}`} />
                <span className="font-mono text-[11px] text-slate-600 truncate max-w-full"><span className="xl:hidden text-slate-400">Type: </span>{d.truck_type || <span className="text-slate-300">—</span>}</span>
                <AiCaptureChip sheet={d.build_sheet_badge} photos={d.photos_badge} unread={d.photos_read?.unread || 0} />
                {canPrintTags && (d.tag_printed_source === "confirm"
                  ? <Badge data-testid={`tag-printed-${d.stock_number}`} className="rounded-lg text-[9px] bg-emerald-600 text-white" title={`Tag printed (confirmed) ${String(d.tag_printed_at).slice(0,10)} (v${d.tag_printed_version || "?"})`}>Tag ✓</Badge>
                  : d.tag_printed_at
                    ? <Badge data-testid={`tag-unverified-${d.stock_number}`} className="rounded-lg text-[9px] bg-slate-200 text-slate-600 border border-slate-300" title="Legacy stamp — a print was recorded before confirm tracking existed. Recorded, not vouched for.">Printed (legacy)</Badge>
                    : <Badge data-testid={`tag-unprinted-${d.stock_number}`} className="rounded-lg text-[9px] bg-slate-400 text-white" title="No confirmed tag print on record">No tag</Badge>)}
              </div>
              {/* STATUS **AND** WHERE IT IS, in the cell that already had room for both — the
                  list stayed silent about location while 241 of 241 sat unplaced, and a silent
                  absence is the one nobody notices. No extra row height: the vehicle cell beside
                  this is taller than the two lines here. */}
              <div className="flex flex-col items-start gap-1">
                <Badge className="rounded-lg bg-slate-800 text-white text-[9px] uppercase font-mono w-fit">{stageLabel(d.status)}</Badge>                {d.location?.pending ? (
                  <span className="flex items-center gap-1 flex-wrap">
                    <span data-testid={`loc-none-${d.stock_number}`} className="font-mono text-[10px] font-bold text-[#B01E28] uppercase">not placed</span>
                    {oneTapOn && (
                      <button type="button" data-testid={`place-one-tap-${d.stock_number}`}
                        onClick={() => placeOneTap(d)} title={`Place in ${sectorWords(sticky)} — one tap, undoable`}
                        className="inline-flex items-center gap-0.5 rounded-md border border-[#E0781A] bg-orange-50 px-1.5 h-6 font-mono text-[10px] font-bold text-[#C76714] hover:bg-orange-100">
                        <MapPin className="w-2.5 h-2.5" />{sticky.code}
                      </button>
                    )}
                    {canPlace && (
                      <PlaceInSectorDialog donorId={d.id} stockNumber={d.stock_number} onPlaced={loadSticky}
                        onDone={() => engine.run()}
                        trigger={<button type="button" data-testid={`place-open-${d.stock_number}`}
                          className="rounded-md border border-slate-300 bg-white px-1.5 h-6 text-[10px] font-semibold text-slate-600 hover:border-slate-500">
                          {oneTapOn ? "elsewhere…" : "place…"}
                        </button>} />
                    )}
                  </span>
                ) : d.location?.missing ? (
                  <span className="flex items-center gap-1 flex-wrap">
                    <span data-testid={`loc-missing-${d.stock_number}`} className="font-mono text-[10px] font-bold text-[#B01E28] uppercase"
                      title="This truck's record points at a location that no longer exists — place it somewhere real.">
                      location record gone
                    </span>
                    {canPlace && (
                      <PlaceInSectorDialog donorId={d.id} stockNumber={d.stock_number} onPlaced={loadSticky}
                        onDone={() => engine.run()}
                        trigger={<button type="button" data-testid={`place-open-${d.stock_number}`}
                          className="rounded-md border border-slate-300 bg-white px-1.5 h-6 text-[10px] font-semibold text-slate-600 hover:border-slate-500">
                          place…
                        </button>} />
                    )}
                  </span>
                ) : canPlace ? (
                  <PlaceInSectorDialog donorId={d.id} stockNumber={d.stock_number} onPlaced={loadSticky}
                    onDone={() => engine.run()}
                    trigger={<button type="button" data-testid={`loc-${d.stock_number}`}
                      title={`${d.location?.path || d.location?.label} (${d.location?.code}) — tap to move it`}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 h-6 text-[10px] text-slate-700 hover:border-[#E0781A] max-w-[12rem]">
                      <MapPin className="w-2.5 h-2.5 text-slate-400 shrink-0" />
                      <span className="truncate">{d.location?.label || d.location?.code}</span>
                      <span className="font-mono text-slate-400 shrink-0">{d.location?.code}</span>
                      {d.location?.holding && <span className="text-[#C76714] shrink-0">· holding</span>}
                    </button>} />
                ) : (
                  <span data-testid={`loc-${d.stock_number}`} className="text-[10px] text-slate-600"
                    title={`${d.location?.path || d.location?.label} (${d.location?.code})`}>
                    {d.location?.label || d.location?.code} <span className="font-mono text-slate-400">{d.location?.code}</span>
                    {d.location?.holding ? <span className="text-[#C76714]"> · holding</span> : null}
                  </span>
                )}
              </div>
              {showCost ? <span className="font-mono text-xs"><span className="xl:hidden text-slate-400">Cost: </span>{d.starting_cost != null ? `$${d.starting_cost}` : "—"}</span> : <span className="hidden xl:block" />}
              </div>
              {/* THINGS YOU PRESS, and nothing else — one tidy line instead of a four-deep stack
                  (his words: "the buttons on the right are stacked too tall"). "Photos" was
                  renamed "Add photos": it opens the UPLOAD picker, while the count on the photo
                  opens the viewer, and two controls labelled the same way read like a duplicate.
                  BELOW DESKTOP the two upload words drop to their icons (the tooltip still says
                  it) so the strip fits beside the facts on a tablet instead of taking its own
                  line — the words are back from xl up, where there is room for them. */}
              <div className="flex flex-wrap items-center gap-1.5 justify-start sm:ml-auto sm:justify-end lg:ml-0 lg:justify-end">
                {canPrintTags && <DonorTagButton donorId={d.id} stockNumber={d.stock_number} compact />}
                {canManage && (
                  <>
                    <PhotoUploader
                      endpoint={`/donors/${d.id}/photos`}
                      onUploaded={(photo) => addDonorPhoto(d.id, photo)}
                      labelClass="hidden xl:inline"
                      testidPrefix={`donor-photos-${d.stock_number}`}
                    />
                    <PhotoUploader
                      variant="document"
                      multiple={false}
                      endpoint={`/donors/${d.id}/build-sheet`}
                      buttonLabel={<span className="hidden xl:inline">Sheet</span>}
                      onUploaded={(r) => { (r?.read_exactly === false ? toast.warning : toast.success)(r?.message || "Build sheet attached"); engine.run(); }}
                      testidPrefix={`build-sheet-${d.stock_number}`}
                    />
                    <Button data-testid={`edit-donor-${d.stock_number}`} size="sm" variant="outline" className="rounded-lg h-7 border-slate-200" onClick={() => openEdit(d)}>Edit</Button>
                    <Button size="sm" variant="outline" className="rounded-lg h-7 w-7 p-0 border-slate-200 text-destructive" title="Archive" onClick={() => archive(d)}><Archive className="w-3.5 h-3.5" /></Button>
                  </>
                )}
              </div>
              </div>
              </div>
            </div>
          ))}
      </div>

      {totalPages > 1 && (
        <div data-testid="donor-pagination" className="flex items-center justify-center gap-3 mt-5">
          <Button data-testid="donor-page-prev" variant="outline" size="sm" className="rounded-lg h-12 px-5"
            disabled={pageNum <= 1} onClick={() => setParam({ page: pageNum - 1 })}>Previous</Button>
          <span className="text-sm text-slate-500" data-testid="donor-page-indicator">Page {pageNum} of {totalPages}</span>
          <Button data-testid="donor-page-next" variant="outline" size="sm" className="rounded-lg h-12 px-5"
            disabled={pageNum >= totalPages} onClick={() => setParam({ page: pageNum + 1 })}>Next</Button>
        </div>
      )}
      </>)}

      <DonorBatchTagDialog open={batchTagOpen} onOpenChange={setBatchTagOpen} donorIds={selectedIds} onPrinted={() => { setSelected({}); engine.run(); }} />
      <DonorBatchTagDialog open={notagBatchOpen} onOpenChange={setNotagBatchOpen} previewItems={noTagItems} onPrinted={() => { setNotagBatchOpen(false); engine.run(); }} />

      <Dialog open={bulkConfirm} onOpenChange={(v) => !v && setBulkConfirm(false)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="bulk-stage-confirm">
          <DialogHeader><DialogTitle className="font-display">Set dismantle stage</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Set <b data-testid="bulk-confirm-count">{selectedIds.length}</b> truck{selectedIds.length === 1 ? "" : "s"} to <b data-testid="bulk-confirm-stage">{stageLabel(bulkStage)}</b>? This only changes the dismantle stage and is logged for each truck.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setBulkConfirm(false)}>Cancel</Button>
            <Button data-testid="bulk-stage-confirm-yes" disabled={bulkSaving} onClick={applyBulkStage} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
              {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Set ${selectedIds.length} to ${stageLabel(bulkStage)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-lg" data-testid="donor-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit truck" : "Add Truck"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60dvh] overflow-y-auto pr-1">
            <div data-testid="donor-asset-toggle">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Asset type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" data-testid="asset-vehicle" onClick={() => setForm({ ...form, asset_class: "vehicle" })}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold text-left ${form.asset_class !== "equipment" ? "border-[#E0781A] bg-orange-50 text-[#C76714]" : "border-slate-200 text-slate-500"}`}>
                  Vehicle / truck<span className="block text-[10px] font-normal">17-char VIN</span></button>
                <button type="button" data-testid="asset-equipment" onClick={() => { setForm({ ...form, asset_class: "equipment", vin_unknown: false }); setVinWarn(null); }}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold text-left ${form.asset_class === "equipment" ? "border-[#E0781A] bg-orange-50 text-[#C76714]" : "border-slate-200 text-slate-500"}`}>
                  Equipment<span className="block text-[10px] font-normal">serial # / no VIN</span></button>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{form.asset_class === "equipment" ? "Serial number" : "VIN"}</Label>
                {form.asset_class !== "equipment" && (
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer select-none">
                    <input type="checkbox" data-testid="donor-vin-unknown" checked={!!form.vin_unknown}
                      onChange={(e) => { const v = e.target.checked; setForm({ ...form, vin_unknown: v }); if (v) setVinWarn(null); }}
                      className="w-3.5 h-3.5 accent-[#E0781A] cursor-pointer" />
                    VIN unknown / unreadable
                  </label>
                )}
              </div>
              <div className="flex gap-2 mt-1">
                <ScanTextInput testid="donor-vin-input" value={form.vin} className="flex-1"
                  disabled={form.vin_unknown && form.asset_class !== "equipment"}
                  onChange={(v) => setForm({ ...form, vin: v.toUpperCase() })}
                  onBlur={form.asset_class !== "equipment" ? checkVin : undefined}
                  placeholder={form.asset_class === "equipment" ? "Serial # (any length, or leave blank)" : (form.vin_unknown ? "No readable VIN" : "17-character VIN")}
                  inputClassName="disabled:bg-slate-100 disabled:text-slate-400" />
                {form.asset_class !== "equipment" && (
                  <Button data-testid="decode-vin-button" type="button" variant="outline" className="rounded-lg border-slate-200 whitespace-nowrap" onClick={decode} disabled={decoding || form.vin_unknown}>
                    {decoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanLine className="w-4 h-4 mr-1" /> Decode</>}
                  </Button>
                )}
              </div>
              {form.asset_class === "equipment" ? (
                <p className="text-[11px] text-slate-400 mt-1">Equipment serial — any length, or leave blank. No 17-character VIN rule applies.</p>
              ) : (
                <>
                  {!form.vin_unknown && form.vin && (["I", "O", "Q"].some((c) => form.vin.toUpperCase().includes(c)) || form.vin.trim().length !== 17) && (
                    <p data-testid="donor-vin-length-error" className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-red-600">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {form.vin.trim().length !== 17 ? `${form.vin.trim().length}/17 characters — a VIN must be exactly 17.` : ""}
                      {["I", "O", "Q"].filter((c) => form.vin.toUpperCase().includes(c)).length ? ` Contains ${["I", "O", "Q"].filter((c) => form.vin.toUpperCase().includes(c)).join(", ")} (never valid in a VIN).` : ""}
                    </p>
                  )}
                  {form.vin_unknown && <p className="text-[11px] text-slate-400 mt-1">Flagged as a no-VIN truck — it won't block or collide with other trucks. Enter Year/Make/Model to help spot possible duplicates.</p>}
                  {vinWarn?.active && (
                    <div data-testid="donor-vin-warn-active" className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2.5 py-1.5 text-[11px] text-red-700">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>VIN already on <button type="button" className="font-bold underline" onClick={() => { setOpen(false); navigate(`/donors/${vinWarn.active.id}`); }}>{vinWarn.active.stock_number}</button>{vinWarn.active.ymm ? ` (${vinWarn.active.ymm})` : ""} — this will be blocked. Open that truck instead.</span>
                    </div>
                  )}
                  {!vinWarn?.active && vinWarn?.archived && (
                    <div data-testid="donor-vin-warn-archived" className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] text-amber-700">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>A scrapped/archived truck ({vinWarn.archived.stock_number}) had this VIN. You can still add it — you'll confirm on save.</span>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Year</Label><Input data-testid="donor-year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Status</Label>
                <Select value={form.status || ""} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="donor-status" className="rounded-lg border-slate-200 mt-1"><SelectValue placeholder="Choose a stage" /></SelectTrigger>
                  <SelectContent className="rounded-lg">
                    {stageCodes.map((s) => <SelectItem key={s} value={s}>{stageLabel(s)}</SelectItem>)}
                    {/* a truck already sitting in a retired stage keeps it selectable, so opening
                        the dialog can never silently move it into a different stage */}
                    {form.status && !stageCodes.includes(form.status) && (
                      <SelectItem value={form.status} data-testid="donor-status-off-pipeline">
                        {stageLabel(form.status)} — not in your pipeline
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Make</Label><Input data-testid="donor-make" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Model</Label><Input data-testid="donor-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
              <div className="col-span-2"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Engine model</Label><Input data-testid="donor-engine" value={form.engine_model} onChange={(e) => setForm({ ...form, engine_model: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
              <div className="col-span-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</Label>
                  {canManageTypes && <button type="button" data-testid="donor-add-type" onClick={addTruckType} className="text-[11px] text-[#E0781A] hover:underline">+ Add type</button>}
                </div>
                <Select value={form.truck_type || "none"} onValueChange={(v) => setForm({ ...form, truck_type: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="donor-type" className="rounded-lg border-slate-200 mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="none">— none —</SelectItem>
                    {truckTypes.map((t) => <SelectItem key={t.id} value={t.name} data-testid={`donor-type-opt-${t.name}`}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Mileage</Label><Input data-testid="donor-mileage" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Engine hours</Label><Input data-testid="donor-hours" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
            </div>
            {showCost && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Purchase price</Label><Input data-testid="donor-purchase" type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Landed cost</Label><Input data-testid="donor-landed" type="number" value={form.landed_cost} onChange={(e) => setForm({ ...form, landed_cost: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
              </div>
            )}
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label><Textarea data-testid="donor-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
            <div className="rounded-lg border border-yellow-300 bg-yellow-50/60 p-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" data-testid="donor-needs-info" checked={!!form.needs_info}
                  onChange={(e) => setForm({ ...form, needs_info: e.target.checked })} className="w-4 h-4 accent-[#C76714] cursor-pointer" />
                <span className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-[#C76714]" />Needs more info</span>
              </label>
              {form.needs_info && (
                <Input data-testid="donor-needs-info-note" value={form.needs_info_note} onChange={(e) => setForm({ ...form, needs_info_note: e.target.value })}
                  placeholder="What's still missing? e.g. confirm engine/trans from tags" className="rounded-lg border-yellow-300 mt-2 text-sm" />
              )}
              <p className="text-[11px] text-slate-500 mt-1.5">Flags this truck in the Trucks-page “Needs attention” worklist until you clear it. Doesn't affect its teardown status.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-donor-button" disabled={saving || (form.asset_class !== "equipment" && !form.vin && !form.vin_unknown)} onClick={() => save(false)} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> {editId ? "Update" : "Create"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DonorDuplicatesDialog open={dupOpen} onOpenChange={setDupOpen} showCost={showCost}
        onMerged={() => { engine.run(); loadDupCount(); }} onOpenDonor={(id) => { setDupOpen(false); navigate(`/donors/${id}`); }} />
    </div>
  );
}
